/**
 * CreditEngineShadowReport — /admin/credit-engine/shadow-report
 *
 * Wave 5-Late Credit Engine propagation — anchor pattern applied.
 * Readiness threshold values (95 / 80) preserved BYTE-EXACT — the
 * traffic-light tone map (green/amber/red/grey) now uses the same
 * HEALTH_TONE convention as the Credit Engine anchor's
 * UnderExtractionHealthCard: pre-tokenised soft/border/ink triads.
 * StatBox → anchor `Stat` primitive. Sub-metric color coding uses
 * `--ds-color-success-muted` / `--ds-color-danger`. Sparkline SVG
 * rewired: line `--ds-color-accent-strong`, target 95%-line
 * `--ds-color-success-muted`, last-point delta color drops onto tone
 * tokens (was `#86EFAC` / `#FCA5A5`).
 *
 * Business logic (window select + parallel shadow-report +
 * cutover-readiness fetch + 30d sparkline gap-handling) preserved
 * BYTE-EXACT. All testids unchanged.
 */
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, TrendingUp, AlertTriangle, Layers } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { GhostButton, Section, SelectInput, Stat } from "@/admin/_primitives";

/* Traffic-light tone triads — threshold values below are BYTE-EXACT. */
const HEALTH_TONE = {
  green: {
    bg: "var(--ds-color-success-muted-soft)",
    border: "var(--ds-color-success-muted)",
    ink: "var(--ds-color-success-muted)",
  },
  amber: {
    bg: "var(--ds-color-warning-soft)",
    border: "var(--ds-color-warning)",
    ink: "var(--ds-color-warning)",
  },
  red: {
    bg: "var(--ds-color-danger-soft)",
    border: "var(--ds-color-danger)",
    ink: "var(--ds-color-danger)",
  },
  grey: {
    bg: "var(--ds-color-neutral-muted-soft)",
    border: "var(--ds-color-admin-border)",
    ink: "var(--ds-color-neutral-muted)",
  },
};

function pctToneKey(pct) {
  if (pct == null) return "grey";
  if (pct >= 95) return "green";
  if (pct >= 80) return "amber";
  return "red";
}





export default function AdminCreditEngineShadowReport() {
  const [report, setReport] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowHours, setWindowHours] = useState(24);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: rd }] = await Promise.all([
        api.get(`/admin/credit-engine/shadow-report?window_hours=${windowHours}`),
        api.get(`/admin/credit-engine/cutover-readiness?window_hours=${windowHours}`),
      ]);
      setReport(r);
      setReadiness(rd);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [windowHours]);

  useEffect(() => {
    reload();
  }, [reload]);

  const pct = readiness?.current?.readiness_pct;
  const tone = HEALTH_TONE[pctToneKey(pct)];

  return (
    <AdminLayout
      title="V2 shadow report"
      subtitle={`Cutover readiness monitor — window: last ${windowHours}h`}
      actions={
        <div className="flex items-center gap-2">
          <SelectInput
            data-testid="shadow-report-window-select"
            value={windowHours}
            onChange={(e) => setWindowHours(parseInt(e.target.value))}
          >
            <option value={24}>24h</option>
            <option value={168}>7d</option>
            <option value={720}>30d</option>
          </SelectInput>
          <GhostButton onClick={reload} data-testid="shadow-report-refresh" className="!py-1 !text-[11px]">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </GhostButton>
        </div>
      }
    >
      <div className="mb-3 max-w-3xl" data-testid="shadow-report-help">
        <AdminPageHelp
          title="V2 shadow report"
          purpose="V2.1 Phase 2 Section J. Every credit-report import with credit_engine_v2 ON is compared against the legacy pipeline. This page surfaces the aggregate parity, mismatches, and Cutover Readiness % across a rolling window. Reads-are-still-legacy — this is a diagnostic view only."
          configures="Nothing. Read-only."
          impact="Zero user-facing impact. Diagnostic reads on credit_engine_events + review_queue."
          safety="Cutover Readiness % never auto-switches the read path. That decision remains manual per cutover_exit_criteria.md."
        />
      </div>

      {/* Cutover Readiness hero card — mirror UnderExtractionHealthCard structure. */}
      <section
        data-testid="cutover-readiness-card"
        className="mb-3 rounded-[var(--ds-radius-card)] overflow-hidden"
        style={{
          background: "var(--ds-color-surface)",
          border: `1px solid ${tone.border}`,
          boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
        }}
      >
        <div className="grid md:grid-cols-[auto_1fr_auto] items-stretch">
          <div
            className="px-4 py-3 md:min-w-[260px]"
            style={{ background: tone.bg, borderRight: `1px solid ${tone.border}` }}
          >
            <div
              className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold"
              style={{ color: tone.ink }}
            >
              <TrendingUp className="w-3 h-3" aria-hidden="true" />
              Cutover Readiness
            </div>
            <div
              data-testid="cutover-readiness-value"
              className="mt-1 text-[40px] leading-tight tabular-nums font-semibold"
              style={{ color: "var(--ds-color-ink)" }}
            >
              {pct != null ? `${pct.toFixed(1)}%` : "—"}
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: "var(--ds-color-ink-muted)" }}>
              cohort of {readiness?.current?.cohort_size ?? 0} imports · target ≥ 95%
            </div>
          </div>
          <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(readiness?.current?.sub_metrics || {}).map(([k, v]) => {
              const target = readiness?.current?.sub_metric_thresholds?.[k];
              const ok = target ? v >= target : true;
              return (
                <div key={k} data-testid={`cutover-submetric-${k}`}>
                  <div
                    className="text-[10px] uppercase tracking-[0.12em] font-semibold"
                    style={{ color: "var(--ds-color-ink-subtle)" }}
                  >
                    {k.replace(/_/g, " ")}
                  </div>
                  <div className="text-[13px] tabular-nums">
                    <span style={{ color: ok ? "var(--ds-color-success-muted)" : "var(--ds-color-danger)" }}>
                      {(v * 100).toFixed(1)}%
                    </span>
                    {target != null && (
                      <span
                        className="text-[10px] ml-1"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        / {(target * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div
            className="px-4 py-3 md:text-right md:min-w-[220px] text-[11px]"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            <div
              className="text-[10px] uppercase tracking-[0.12em] font-semibold"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Last cron
            </div>
            <div style={{ color: "var(--ds-color-ink)" }}>
              {readiness?.last_cron_run?.completed_at
                ? new Date(readiness.last_cron_run.completed_at).toISOString().slice(0, 16).replace("T", " ") + " UTC"
                : "Never"}
            </div>
            {readiness?.last_cron_run?.success === false && (
              <div style={{ color: "var(--ds-color-danger)" }} className="mt-0.5">
                Failed · retry #{readiness.last_cron_run.retry_count}
              </div>
            )}
            {readiness?.last_cron_run?.success === true && (
              <div style={{ color: "var(--ds-color-success-muted)" }} className="mt-0.5">
                Healthy
              </div>
            )}
          </div>
        </div>
        <ReadinessSparkline history={readiness?.history_30d || []} />
      </section>

      {/* Top-line counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Stat testid="shadow-processed-today" label="Processed" value={report?.processed_today ?? "—"} highlight />
        <Stat testid="shadow-with-delta" label="Shadow evaluated" value={report?.with_shadow_delta ?? "—"} />
        <Stat testid="shadow-mismatches" label="Mismatches" value={report?.mismatches_count ?? "—"} />
        <Stat
          testid="shadow-under-extraction-30d"
          label="Under-extractions (30d)"
          value={report?.under_extraction_trend?.find((t) => t.window_days === 30)?.under_extraction_count ?? "—"}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <VarianceList
          title="Largest account-count variance"
          icon={Layers}
          testid="top-account-variance"
          rows={report?.top_account_variance}
        />
        <VarianceList
          title="Largest negative-item variance"
          icon={AlertTriangle}
          testid="top-negative-variance"
          rows={report?.top_negative_variance}
        />
      </div>

      {loading && (
        <div
          data-testid="shadow-report-loading"
          className="text-[13px] mt-3"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          Loading…
        </div>
      )}
    </AdminLayout>
  );
}

/* 30-day readiness sparkline — palette rewired to admin tokens. */
function ReadinessSparkline({ history }) {
  const cleaned = (history || []).map((h) => ({
    date: h.date,
    value: typeof h.readiness_pct === "number" ? h.readiness_pct : null,
    cohort: h.cohort_size || 0,
  }));
  const points = cleaned.filter((p) => p.value != null && p.cohort > 0);
  if (!cleaned.length) {
    return (
      <div
        className="px-4 py-3 border-t text-[11px]"
        style={{
          borderColor: "var(--ds-color-admin-border)",
          color: "var(--ds-color-ink-muted)",
        }}
        data-testid="cutover-readiness-sparkline-empty"
      >
        No 30-day readiness history yet — first cron cohort will populate this trend.
      </div>
    );
  }
  const width = 640;
  const height = 60;
  const padX = 4;
  const padY = 6;
  const n = cleaned.length;
  const step = n > 1 ? (width - padX * 2) / (n - 1) : 0;
  const values = points.map((p) => p.value);
  const min = Math.min(80, ...values);
  const max = 100;
  const y = (v) =>
    padY + (1 - (Math.max(min, Math.min(max, v)) - min) / (max - min)) * (height - padY * 2);
  const x = (i) => padX + i * step;

  let path = "";
  let started = false;
  cleaned.forEach((p, i) => {
    if (p.value == null) {
      started = false;
      return;
    }
    path += `${started ? "L" : "M"}${x(i).toFixed(2)},${y(p.value).toFixed(2)} `;
    started = true;
  });

  const first = points[0]?.value;
  const last = points[points.length - 1]?.value;
  const delta = first != null && last != null ? last - first : 0;
  const deltaColor = delta >= 0 ? "var(--ds-color-success-muted)" : "var(--ds-color-danger)";
  const targetY = y(95);

  return (
    <div
      className="px-4 py-3 border-t"
      style={{ borderColor: "var(--ds-color-admin-border)" }}
      data-testid="cutover-readiness-sparkline"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div
          className="text-[10px] uppercase tracking-[0.16em] font-semibold"
          style={{ color: "var(--ds-color-ink-subtle)" }}
        >
          30-day readiness trend
        </div>
        <div
          className="text-[11px] tabular-nums"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          {points.length} data-days ·{" "}
          <span style={{ color: deltaColor }} data-testid="cutover-readiness-sparkline-delta">
            {delta >= 0 ? "+" : ""}
            {delta.toFixed(1)} pp
          </span>{" "}
          vs 30d start
        </div>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="w-full h-[60px]"
        role="img"
        aria-label="Cutover readiness 30-day trend sparkline"
      >
        <line
          x1={padX}
          y1={targetY}
          x2={width - padX}
          y2={targetY}
          stroke="var(--ds-color-success-muted)"
          strokeOpacity="0.4"
          strokeWidth="1"
          strokeDasharray="3 3"
        />
        <text
          x={width - padX - 2}
          y={targetY - 2}
          textAnchor="end"
          fontSize="8"
          fill="var(--ds-color-success-muted)"
          fillOpacity="0.75"
        >
          95%
        </text>
        <path
          d={path}
          stroke="var(--ds-color-accent-strong)"
          strokeWidth="1.75"
          fill="none"
        />
        {cleaned.map((p, i) =>
          p.value == null ? null : (
            <circle
              key={i}
              cx={x(i).toFixed(2)}
              cy={y(p.value).toFixed(2)}
              r={i === n - 1 ? 3 : 1.5}
              fill={i === n - 1 ? deltaColor : "var(--ds-color-accent-strong)"}
            >
              <title>{`${p.date}: ${p.value.toFixed(1)}% (cohort ${p.cohort})`}</title>
            </circle>
          ),
        )}
      </svg>
      <div
        className="flex justify-between text-[10px] tabular-nums"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        <span>{cleaned[0]?.date}</span>
        <span>{cleaned[cleaned.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function VarianceList({ title, icon: Icon, testid, rows }) {
  return (
    <Section title={title} icon={Icon || Layers} testid={testid} bodyClassName="p-0">
      {rows?.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr
                className="text-left text-[10px] uppercase tracking-[0.14em] font-semibold"
                style={{
                  color: "var(--ds-color-ink-subtle)",
                  background: "var(--ds-color-admin-surface-muted)",
                  borderBottom: "1px solid var(--ds-color-admin-border)",
                }}
              >
                <th className="px-4 py-2 font-semibold">Report</th>
                <th className="px-4 py-2 font-semibold text-right">Δ</th>
                <th className="px-4 py-2 font-semibold text-right">V2 accts</th>
                <th className="px-4 py-2 font-semibold text-right">Legacy</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const isLast = idx === rows.length - 1;
                return (
                  <tr
                    key={row.event_id}
                    style={{
                      borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                    }}
                  >
                    <td
                      className="px-4 py-1.5 text-[11px] font-mono truncate max-w-[180px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      {String(row.report_id).slice(-8)}
                    </td>
                    <td
                      className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                      style={{ color: "var(--ds-color-ink)" }}
                    >
                      {row.variance}
                    </td>
                    <td
                      className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      {row.delta?.v2_account_count ?? "—"}
                    </td>
                    <td
                      className="px-4 py-1.5 text-[12px] text-right tabular-nums font-mono"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      {row.delta?.legacy_account_count ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          className="px-4 py-6 text-[13px] text-center"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          No variance in this window.
        </div>
      )}
    </Section>
  );
}
